// Fix 2 Case B — aspect-aware slotting (pure decision logic).
//
// The three renderers (editor / preview / PDF) all COVER-FIT a photo to its
// slot. A landscape (wide) photo dropped into a portrait-shaped slot therefore
// loses its left + right edges — exactly the crop the customer complained
// about. This module decides, for ONE page, a layout + photo order that keeps
// every landscape in a WIDE slot, choosing among the three landscape-friendly
// layouts:
//
//   H1 = 2-up / wide-top   both slots are full-width → landscapes already fit
//   H2 = 3-up              1 WIDE photo on top    + 2 portraits below
//   H3 = 3-up-flip         2 portraits on top     + 1 WIDE photo on the bottom
//
// It is PURE + per-page: it returns the (possibly changed) layout and a
// REORDERING of the SAME photo ids — it never drops, adds, or moves a photo to
// another page. The caller (`enforceLandscapeSlots` in AlbumEditorPage) supplies
// the landscape test + the layout's slot aspect ratios, so this stays decoupled
// from the editor's huge module and is unit-testable on its own.
//
// `preferH3` selects the 3-up-flip variant (wide on the bottom) over 3-up (wide
// on top) for the 1-landscape / 2-portrait case. The caller alternates it across
// qualifying spreads so the album doesn't render every landscape page the same
// way (the customer approved this "rearranging" for variety — Option B).

export interface SlotArrangement {
  layout: string;
  photos: string[];
}

export function chooseLandscapeArrangement(
  photos: string[],
  layout: string,
  slotARs: number[] | undefined,
  isLandscape: (id: string) => boolean,
  preferH3: boolean,
): SlotArrangement {
  // Only reorder a fully-filled page of ≥2 photos. A page with an empty hole
  // would have been downgraded by snapLayoutToPhotoCount already; bail rather
  // than risk changing the photo count.
  if (photos.length < 2 || !photos.every(Boolean)) return { layout, photos };
  const ids = photos;
  const nLand = ids.filter(isLandscape).length;
  if (nLand === 0) return { layout, photos }; // no landscape → nothing to enforce

  // ── 3 photos, exactly 1 landscape → H2 (wide top) or H3 (wide bottom), with
  // the landscape in the single wide slot. Both place it correctly; preferH3
  // just alternates the look.
  if (
    ids.length === 3 &&
    nLand === 1 &&
    (layout === '3-up' || layout === '3-up-flip' || layout === '4-up')
  ) {
    const land = ids.find(isLandscape)!;
    const rest = ids.filter((x) => x !== land);
    return preferH3
      ? { layout: '3-up-flip', photos: [rest[0], rest[1], land] } // wide = slot 2 (bottom)
      : { layout: '3-up', photos: [land, rest[0], rest[1]] }; // wide = slot 0 (top)
  }

  // ── General case: reorder within the current layout so landscapes occupy the
  // wide slots (AR ≥ 1) and portraits the narrow ones. Pure permutation.
  if (!slotARs) return { layout, photos }; // wide-top: both slots full-width → ok as-is
  const n = ids.length;
  const wide: number[] = [];
  const narrow: number[] = [];
  slotARs.forEach((ar, i) => {
    if (i < n) (ar >= 1 ? wide : narrow).push(i);
  });
  // All-wide (2-up: landscapes already fine) or all-narrow (4-up: no wide slot
  // to move them into) → reordering can't improve anything. Leave it.
  if (wide.length === 0 || narrow.length === 0) return { layout, photos };
  const order = [...wide, ...narrow]; // fill wide slots first
  if (order.length !== n) return { layout, photos }; // ragged → stay safe
  const queue = [...ids.filter(isLandscape), ...ids.filter((x) => !isLandscape(x))];
  const out = new Array<string>(n);
  order.forEach((slot, k) => {
    out[slot] = queue[k];
  });
  return { layout, photos: out };
}
